import { Box, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

interface RackSegmentProps {
  /** All-caps eyebrow above the value (5 chars max — keep it tight). */
  label: string;
  /** Click target — turns the whole segment into a button without
   *  forcing every consumer to wrap in <ButtonBase>. */
  onClick?: () => void;
  /** Optional fixed width. Project / draft / credits segments all set
   *  this so the rack stops jumping when content shifts. */
  width?: number;
  /** Set true to keep the segment in the layout but visually faded —
   *  the calm null state when no plugin is connected, no draft active,
   *  etc. Distinct from "missing" which is `return null`. */
  empty?: boolean;
  /** Accessible name for the segment. The eyebrow label + value read
   *  fine for sighted users, but a screen reader announcing a clickable
   *  segment wants an explicit, action-shaped name (e.g. "Open project
   *  overview") rather than the concatenated visual text. */
  ariaLabel?: string;
  children?: ReactNode;
}

/**
 * The shared chassis every Instrument Rack segment renders inside —
 * eyebrow + value column with a generous click target and a phosphor
 * underline that grows on hover. Mirrors the look of a rack-mount
 * instrument panel: the label is engraved, the value is the readout.
 */
export function RackSegment({
  label,
  onClick,
  width,
  empty,
  ariaLabel,
  children,
}: RackSegmentProps) {
  return (
    <Box
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? ariaLabel : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={(t) => ({
        position: "relative",
        height: "100%",
        width,
        minWidth: width,
        flex: width ? "0 0 auto" : "0 1 auto",
        px: 1.75,
        display: "flex",
        alignItems: "center",
        cursor: onClick ? "pointer" : "default",
        opacity: empty ? 0.55 : 1,
        transition: `opacity ${t.motion.fast}, background-color ${t.motion.fast}`,
        "&:hover": onClick
          ? {
              backgroundColor: t.palette.accent.softer,
              "& .rack-underline": { transform: "scaleX(1)" },
            }
          : undefined,
        // Pressed feedback — a hair of phosphor lift so a click reads as
        // a click, not a dead zone.
        "&:active": onClick
          ? { backgroundColor: t.palette.accent.soft }
          : undefined,
        // Keyboard focus was invisible — hover styling only fires on
        // pointer. Mirror the hover underline on focus-visible and add a
        // crisp inset ring so tab-through is legible. Outline is inset so
        // it never clips against the rack's top hairline.
        outline: "none",
        "&:focus-visible": onClick
          ? {
              backgroundColor: t.palette.accent.softer,
              boxShadow: `inset 0 0 0 2px ${t.palette.accent.main}`,
              "& .rack-underline": { transform: "scaleX(1)" },
            }
          : undefined,
        // The phosphor-cyan underline that grows on hover. SVG-free —
        // a 1px transform-scaled bar costs nothing.
        "&::after": onClick
          ? {
              content: '""',
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 6,
              height: "1px",
              background: t.palette.accent.main,
              transform: "scaleX(0)",
              transformOrigin: "left center",
              transition: `transform ${t.motion.base}`,
            }
          : undefined,
      })}
    >
      <Stack spacing={0.25} sx={{ width: "100%", minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: (t) => t.fonts.mono,
            fontSize: "0.625rem",
            fontWeight: 500,
            color: "text.secondary",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
        {children}
      </Stack>
      <Box
        className="rack-underline"
        sx={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 6,
          height: "1px",
          background: "transparent",
        }}
      />
    </Box>
  );
}
